import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subscription, filter, map, take } from 'rxjs';

import { ConfigService, isPresent, RootPaths, unsubscribe, WindowRef } from '@fcom/core/index';
import { Category, FinnairBoundItem, FinnairOrder, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { BoundType, IdAndHash } from '@fcom/dapi/index';
import { ToasterService } from '@fcom/common/services';
import { containsUnpaidSeats, getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { createEmptySeatServices, isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { GaContext, LoaderType, ToasterMessage } from '@fcom/common/interfaces';
import {
  CommonBookingFeatureState,
  FinnairServiceItemWithIcon,
  OrderActions,
  OrderStatus,
  PaymentFlow,
  paymentRedirectionUrl,
  PaymentService,
  PaymentStatus,
  paymentStatus,
  SeatMapActions,
} from '@fcom/common-booking/index';
import { NotificationTheme } from '@fcom/ui-components/index';
import { LanguageService } from '@fcom/ui-translate/index';

import { ManageBookingFeatureState } from '../../../interfaces';
import { ManageBookingService } from '../../../services';
import { CheckInStep } from '../../../enums';
import { CheckInService } from '../../../services/check-in.service';

type ModalStates = 'clearing' | 'payment' | 'off';

@Component({
  selector: 'fin-check-in-kyt-route',
  templateUrl: 'check-in-know-your-trip-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInKnowYourTripRouteComponent implements OnInit, OnDestroy {
  readonly OrderStatus = OrderStatus;
  readonly GaContext = GaContext;
  readonly BoundType = BoundType;
  readonly LoaderType = LoaderType;

  order$: Observable<FinnairOrder>;
  orderWithCheckInPax$: Observable<FinnairOrder>;
  orderStatus$: Observable<OrderStatus>;
  bound$: Observable<FinnairBoundItem>;
  selectedForCheckInPassengers$: Observable<FinnairPassengerItem[]>;
  services$: Observable<FinnairServiceItemWithIcon[]>;
  hasUnpaidSeats$: Observable<boolean>;
  servicesWithEmptySeats$: Observable<FinnairServiceItemWithIcon[]>;

  loadingModal$: BehaviorSubject<ModalStates> = new BehaviorSubject<ModalStates>('off');
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  clearingSeatsInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  updatingPassengers: string[] = [];
  checkoutUrl: string;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private toasterService: ToasterService,
    private configService: ConfigService,
    private manageBookingService: ManageBookingService,
    private paymentService: PaymentService,
    private windowRef: WindowRef,
    private router: Router,
    private languageService: LanguageService,
    private activatedRoute: ActivatedRoute,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.checkoutUrl = this.configService.cfg.checkoutUrl;
    this.order$ = this.checkInService.getOrder();
    this.orderStatus$ = this.checkInService.getOrderStatus();
    this.bound$ = this.checkInService.getBound();
    this.selectedForCheckInPassengers$ = this.checkInService.getPassengers();
    this.orderWithCheckInPax$ = combineLatest([
      this.order$,
      this.selectedForCheckInPassengers$.pipe(map((pax) => pax.map((p) => p.id))),
    ]).pipe(
      map(([order, paxIds]) => {
        return {
          ...order,
          passengers: order.passengers.filter((passenger) => paxIds.includes(passenger.id)),
        };
      })
    );
    this.services$ = this.checkInService.getServices();
    this.servicesWithEmptySeats$ = combineLatest([
      this.services$,
      this.bound$,
      this.selectedForCheckInPassengers$.pipe(map((passengers) => passengers.map((p) => p.id))),
      this.order$,
      this.languageService.translate('MMB.includedProducts.seat.title'),
    ]).pipe(map(([s, bound, paxIds, order, title]) => createEmptySeatServices(s, bound, paxIds, order, title)));
    this.hasUnpaidSeats$ = this.order$.pipe(map((order) => containsUnpaidSeats(order)));

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          take(1),
          filter((data) => isPresent(data?.isPaymentError))
        )
        .subscribe(() => {
          const message: ToasterMessage = {
            id: 'checkInPaymentResult',
            theme: NotificationTheme.ALERT,
            contentKey: 'MMB.checkIn.payment.error',
          };
          this.toasterService.addMessageToQueue(message);
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  confirmStep(): void {
    this.checkInService.triggerNextCheckInStep();
  }

  goToSeatmapStep(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/check-in/step/${CheckInStep.SEATMAP}`
    );
  }

  clearPendingSeats(): void {
    this.subscriptions.add(
      combineLatest([this.selectedForCheckInPassengers$, this.bound$])
        .pipe(take(1))
        .subscribe(([checkInPassengers, bound]) => {
          this.loadingModal$.next('clearing');
          bound.itinerary
            .filter(isFlight)
            .map((flight) => flight.id)
            .forEach((flightId) => {
              checkInPassengers
                .map((pax) => pax.id)
                .forEach((travelerId) => {
                  this.store$.dispatch(
                    SeatMapActions.setSeatForFlight({
                      flightId,
                      travelerId,
                      seat: {},
                    })
                  );
                });
            });
          const [serviceRequests] = getServiceData(
            this.store$,
            Category.SEAT,
            [bound],
            bound.itinerary.filter(isFlight)
          );
          const didOperation = this.manageBookingService.setServices(serviceRequests, Category.SEAT, {
            next: () => {
              this.loadingModal$.next('off');
              this.toasterService.addMessageToQueue({
                id: 'checkIn',
                theme: NotificationTheme.SUCCESS,
                keepMessageAfterNavigation: false,
                contentKey: 'MMB.checkIn.notifications.info.clearingSeats',
              });
            },
            error: () => {
              this.loadingModal$.next('off');
              this.toasterService.addMessageToQueue({
                id: 'checkIn',
                theme: NotificationTheme.ALERT,
                keepMessageAfterNavigation: true,
                contentKey: 'MMB.checkIn.notifications.error.clearingSeats',
              });
            },
          });

          if (!didOperation) {
            this.loadingModal$.next('off');
          }
        })
    );
  }

  initializePayment(orderFetchInfo: IdAndHash): void {
    this.loadingModal$.next('payment');
    this.store$.dispatch(
      OrderActions.setOrderFetchInformation({
        orderInformation: { id: orderFetchInfo.id, hash: orderFetchInfo.hash },
      })
    );

    this.subscriptions.add(
      this.store$.pipe(paymentRedirectionUrl(), filter(isPresent), take(1)).subscribe((receivedUrl: string) => {
        this.windowRef.nativeWindow.location.href = receivedUrl;
      })
    );

    this.subscriptions.add(
      this.store$.pipe(paymentStatus()).subscribe((status: PaymentStatus) => {
        if (status === PaymentStatus.INITIAL) {
          this.paymentService.queuePaymentInit('web_checkin', PaymentFlow.CHECK_IN_FUNNEL);
        } else if (status === PaymentStatus.TECHNICAL_ERROR) {
          this.loadingModal$.next('off');
          this.toasterService.addMessageToQueue({
            id: 'checkIn',
            theme: NotificationTheme.ALERT,
            keepMessageAfterNavigation: true,
            contentKey: 'MMB.checkIn.notifications.error.paymentInitialization',
          });
        }
      })
    );
  }
}
